// b99.9.82/.90 — Controls UI for TripOverviewMap.
// Two variants:
//   * variant="fullscreen" → full top bar with title, layer selector,
//     visibility menu, altitude/histogram toggles, close button.
//   * variant="inline"     → compact icon cluster floating top-right
//     over the map (layer, visibility menu, altitude, histogram,
//     fullscreen-open).
//
// All state lives in the orchestrator; this component is pure
// presentation. `activePanel` is "profile" | "histogram" | null and
// keeps the two altitude visualisations mutually exclusive.
//
// b99.10.41 (2026-05-21) — Os 2 botões inline (etiquetas + setas)
// foram absorvidos pelo `MapVisibilityMenu` partilhado, agrupando os
// 4 toggles num único trigger `Eye` (Início/fim · Paragens ·
// Etiquetas · Setas). Junções não aplicam aqui — são uma noção
// exclusiva do Editor que combina etapas importadas com pinos bi-cor.

import { Button } from "@/components/ui/button";
import { Maximize2, Minimize2, Mountain, BarChart3, Locate } from "lucide-react";
import { useTranslation } from "react-i18next";
import LayerSelector from "../mapShell/LayerSelector";
import MapVisibilityMenu from "../maps/MapVisibilityMenu";

export default function TripOverviewControls({
  variant,
  // Trip meta (fullscreen only)
  tripName,
  // Layer selector
  mapView,
  setMapView,
  // Visibility toggles
  tooltipsVisible,
  toggleTooltips,
  arrowsVisible,
  toggleArrows,
  showStartEnd,
  setShowStartEnd,
  showWaypoints,
  setShowWaypoints,
  poiFilterItems = [],
  // Altitude / histogram (mutually exclusive)
  hasElevationData,
  activePanel,
  setActivePanel,
  // Fullscreen open/close
  onOpenFullscreen,
  onCloseFullscreen,
  // b99.9.100 — Recenter button: re-fits the map to the full trip extent.
  onRecenter,
}) {
  const { t } = useTranslation("common");
  const showElevation = activePanel === "profile";
  const showHistogram = activePanel === "histogram";
  const toggleProfile = () =>
    setActivePanel((p) => (p === "profile" ? null : "profile"));
  const toggleHistogram = () =>
    setActivePanel((p) => (p === "histogram" ? null : "histogram"));

  const visibilityItems = [
    { key: "start-end", label: t("map.controls.start_end", "Início e fim"), checked: showStartEnd, onCheckedChange: setShowStartEnd },
    { key: "waypoints", label: t("map.controls.stops", "Paragens"), checked: showWaypoints, onCheckedChange: setShowWaypoints },
    { key: "sep-1", separator: true },
    { key: "labels", label: t("map.controls.labels", "Etiquetas"), checked: tooltipsVisible, onCheckedChange: toggleTooltips },
    { key: "arrows", label: t("map.controls.arrows", "Setas de direção"), checked: arrowsVisible, onCheckedChange: toggleArrows },
    ...poiFilterItems,
  ];

  if (variant === "fullscreen") {
    return (
      <div className="min-h-12 py-1.5 flex items-center gap-1.5 px-3 border-b border-border bg-card/95 backdrop-blur-sm flex-shrink-0" data-testid="trip-overview-fullscreen-topbar">
        {/* Brand mark — pairs with the footer to brand the standalone
            fullscreen canvas, aligned with the Route Editor fullscreen. */}
        <img
          src="/logo-mototrips.png"
          alt="MotoTrips"
          className="h-11 w-auto object-contain mr-1.5 shrink-0 select-none pointer-events-none"
          data-testid="trip-overview-fullscreen-logo"
        />
        <div className="flex flex-col leading-tight min-w-0 max-w-[50%]">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
            {t("map.controls.full_route", "MAPA TOTAL")}
          </span>
          {tripName && (
            <span className="text-sm font-semibold truncate text-orange-500" title={tripName}>
              {tripName}
            </span>
          )}
        </div>
        <div className="flex-1" />
        <LayerSelector
          view={mapView}
          onChange={setMapView}
          testIdPrefix="trip-overview-layer"
          variant="header"
          hideOnMobile={false}
        />
        {onRecenter && (
          <button
            type="button"
            onClick={onRecenter}
            className="hover:bg-muted rounded-lg p-2 text-foreground"
            data-testid="trip-overview-recenter-fullscreen"
            aria-label={t("map.controls.recenter", "Centrar na totalidade da rota")}
            title={t("map.controls.recenter", "Centrar na totalidade da rota")}
          >
            <Locate className="h-4 w-4" />
          </button>
        )}
        <MapVisibilityMenu
          testIdPrefix="trip-overview-visibility"
          items={visibilityItems}
          variant="icon"
        />
        {hasElevationData && (
          <>
            <button
              type="button"
              onClick={toggleProfile}
              className={`hover:bg-muted rounded-lg p-2 ${showElevation ? "text-orange-500" : "text-foreground"}`}
              data-testid="trip-overview-elevation-toggle"
              aria-label={showElevation ? t("map.controls.hide_profile", "Esconder perfil de altitude") : t("map.controls.show_profile", "Mostrar perfil de altitude")}
              title={showElevation ? t("map.controls.hide_profile", "Esconder perfil de altitude") : t("map.controls.show_profile", "Mostrar perfil de altitude")}
              aria-pressed={showElevation}
            >
              <Mountain className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={toggleHistogram}
              className={`hover:bg-muted rounded-lg p-2 ${showHistogram ? "text-orange-500" : "text-foreground"}`}
              data-testid="trip-overview-histogram-toggle"
              aria-label={showHistogram ? t("map.controls.hide_histogram", "Esconder histograma de altitude") : t("map.controls.show_histogram", "Mostrar histograma de altitude")}
              title={showHistogram ? t("map.controls.hide_histogram", "Esconder histograma de altitude") : t("map.controls.show_histogram", "Mostrar histograma de altitude")}
              aria-pressed={showHistogram}
            >
              <BarChart3 className="h-4 w-4" />
            </button>
          </>
        )}
        <button
          type="button"
          onClick={onCloseFullscreen}
          className="hover:bg-muted rounded-lg p-2"
          data-testid="trip-overview-fullscreen-close"
          aria-label={t("map.controls.exit_fullscreen", "Sair de ecrã inteiro")}
          title={t("map.controls.exit_fullscreen_title", "Sair de ecrã inteiro (Esc)")}
        >
          <Minimize2 className="h-4 w-4" />
        </button>
      </div>
    );
  }

  // variant === "inline" — floating cluster top-right over the map
  return (
    <div className="absolute top-2 right-2 z-[1000] flex gap-1">
      <LayerSelector
        view={mapView}
        onChange={setMapView}
        testIdPrefix="trip-overview-layer-inline"
      />
      <MapVisibilityMenu
        testIdPrefix="trip-overview-visibility-inline"
        items={visibilityItems}
        variant="button"
      />
      {onRecenter && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="bg-background/90 backdrop-blur-sm"
          onClick={onRecenter}
          data-testid="trip-overview-recenter-inline"
          aria-label={t("map.controls.recenter", "Centrar na totalidade da rota")}
          title={t("map.controls.recenter", "Centrar na totalidade da rota")}
        >
          <Locate className="h-4 w-4" />
        </Button>
      )}
      {hasElevationData && (
        <>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={`bg-background/90 backdrop-blur-sm hidden md:inline-flex ${showElevation ? "text-orange-500" : "text-foreground"}`}
            onClick={toggleProfile}
            data-testid="trip-overview-elevation-toggle-inline"
            aria-label={showElevation ? t("map.controls.hide_profile", "Esconder perfil de altitude") : t("map.controls.show_profile", "Mostrar perfil de altitude")}
            title={showElevation ? t("map.controls.hide_profile", "Esconder perfil de altitude") : t("map.controls.show_profile", "Mostrar perfil de altitude")}
            aria-pressed={showElevation}
          >
            <Mountain className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={`bg-background/90 backdrop-blur-sm hidden md:inline-flex ${showHistogram ? "text-orange-500" : "text-foreground"}`}
            onClick={toggleHistogram}
            data-testid="trip-overview-histogram-toggle-inline"
            aria-label={showHistogram ? t("map.controls.hide_histogram", "Esconder histograma de altitude") : t("map.controls.show_histogram", "Mostrar histograma de altitude")}
            title={showHistogram ? t("map.controls.hide_histogram", "Esconder histograma de altitude") : t("map.controls.show_histogram", "Mostrar histograma de altitude")}
            aria-pressed={showHistogram}
          >
            <BarChart3 className="h-4 w-4" />
          </Button>
        </>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="bg-background/90 backdrop-blur-sm hidden md:inline-flex"
        onClick={onOpenFullscreen}
      >
        <Maximize2 className="h-4 w-4" />
      </Button>
    </div>
  );
}
